import { ModelApprovalable, VoidableType } from 'api-hooks/general/model';
import { useVoid } from 'api-hooks/general/mutation';
import { AuthorizationRules } from 'common/constants';
import {
  SaleStatusEnum,
  PurchaseStatusEnum,
  ReleaseStatusEnum,
  TourStatusEnum,
  TransactionalStatusEnum,
  ExpenseIncomeStatusEnum,
  ProjectStatusEnum,
  RefundStatusEnum,
} from 'common/constants/enum';
import DeleteButton from 'components/common/delete-button';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useApprovalAction from './use-approval-action';
import { useAuthorization } from './use-authorization';
import useDialog from './use-dialog';
import useDismiss from './use-dismiss';

export function isDataActive(
  status:
    | ExpenseIncomeStatusEnum
    | SaleStatusEnum
    | PurchaseStatusEnum
    | TransactionalStatusEnum
    | ReleaseStatusEnum
    | TourStatusEnum
    | ProjectStatusEnum
    | RefundStatusEnum
    | undefined,
) {
  switch (status) {
    case TourStatusEnum.Active: // tour
    case SaleStatusEnum.Active: //sale
    case SaleStatusEnum.OpenTicket: //sale
    case PurchaseStatusEnum.Pending: //purchase
    case ReleaseStatusEnum.Requested: //release refund
      return true;
    case SaleStatusEnum.Draft:
    case SaleStatusEnum.Waiting:
    case SaleStatusEnum.Archived:
    case SaleStatusEnum.Processed:
    case SaleStatusEnum.Submitted:
    case SaleStatusEnum.PendingApproval:
    case SaleStatusEnum.RejectedApproval:
    case SaleStatusEnum.RescheduleDraft:
    case SaleStatusEnum.Rescheduling:
    case SaleStatusEnum.Rescheduled:
    case SaleStatusEnum.PendingRescheduleApproval:
    case SaleStatusEnum.RejectedRescheduleApproval:
    case SaleStatusEnum.VoidDraft:
    case SaleStatusEnum.PendingVoidApproval:
    case SaleStatusEnum.Void:
    case SaleStatusEnum.Completed:
    case SaleStatusEnum.Refund:
    case SaleStatusEnum.RevertVoidDraft:
    case SaleStatusEnum.PendingRevertVoidApproval:
    case ReleaseStatusEnum.DraftRefund:
    case ReleaseStatusEnum.PendingRefund:
    case ReleaseStatusEnum.RejectRefund:
    case ReleaseStatusEnum.DraftCancelRefund:
    case ReleaseStatusEnum.PendingCancelRefund:
    case ReleaseStatusEnum.CancelRefund:
    case ReleaseStatusEnum.PendingReleaseApproval:
    case ReleaseStatusEnum.RejectedReleaseApproval:
    case TourStatusEnum.CancelDraft:
    case TourStatusEnum.PendingCancelApproval:
    case TourStatusEnum.Canceled:
    case TourStatusEnum.CloseDraft:
    case TourStatusEnum.PendingCloseApproval:
    case TourStatusEnum.Closed:
    case TourStatusEnum.RevertCloseDraft:
    case TourStatusEnum.PendingRevertCloseApproval:
    default:
      return false;
  }
}

export default function useVoidAction(
  type: VoidableType,
  data?: ModelApprovalable,
) {
  const voidMutate = useVoid();
  const { t } = useTranslation();
  const dialog = useDialog();
  const { can } = useAuthorization();

  const { onRequestApproval } = useApprovalAction(type, data);
  const onDismiss = useDismiss(type);
  const id = data?.id;
  const status = data?.status;

  const isActive = React.useMemo(() => {
    return isDataActive(status);
  }, [status]);

  const permission = React.useMemo(() => {
    switch (type) {
      case 'account-payables':
        return AuthorizationRules.AccountPayablesVoid;
      case 'account-receivables':
        return AuthorizationRules.AccountReceivablesVoid;
      case 'activity-purchases':
        return AuthorizationRules.ActivityPurchasesVoid;
      case 'activity-sales':
        return AuthorizationRules.ActivitySalesVoid;
      case 'balance-adjustments':
        return AuthorizationRules.BalanceAdjustmentsVoid;
      case 'bank-transactions':
        return AuthorizationRules.BankTransactionsVoid;
      case 'cash-transactions':
        return AuthorizationRules.CashTransactionsVoid;
      case 'cruise-purchases':
        return AuthorizationRules.CruisePurchasesVoid;
      case 'cruise-sales':
        return AuthorizationRules.CruiseSalesVoid;
      case 'document-purchases':
        return AuthorizationRules.DocumentPurchasesVoid;
      case 'document-sales':
        return AuthorizationRules.DocumentSalesVoid;
      case 'flight-purchases':
        return AuthorizationRules.FlightPurchasesVoid;
      case 'flight-sales':
        return AuthorizationRules.FlightSalesVoid;
      case 'hotel-purchases':
        return AuthorizationRules.HotelPurchasesVoid;
      case 'hotel-sales':
        return AuthorizationRules.HotelSalesVoid;
      case 'order-adjustments':
        return AuthorizationRules.OrderAdjustmentsVoid;
      case 'other-expenses':
        return AuthorizationRules.OtherExpensesVoid;
      case 'other-incomes':
        return AuthorizationRules.OtherIncomesVoid;
      case 'product-purchases':
        return AuthorizationRules.ProductPurchasesVoid;
      case 'product-sales':
        return AuthorizationRules.ProductSalesVoid;
      case 'receipts':
        return AuthorizationRules.ReceiptsVoid;
      case 'tour-sales':
        return AuthorizationRules.AccountPayablesVoid;
      case 'transfers':
        return AuthorizationRules.TransfersVoid;
    }
  }, [type]);

  const purchaseCore = React.useMemo(() => {
    switch (type) {
      case 'activity-purchases':
      case 'cruise-purchases':
      case 'document-purchases':
      case 'flight-purchases':
      case 'hotel-purchases':
      case 'product-purchases':
        return AuthorizationRules.PurchasesCore;
      default:
        return permission;
    }
  }, [permission, type]);

  const salesCore = React.useMemo(() => {
    switch (type) {
      case 'activity-sales':
      case 'cruise-sales':
      case 'document-sales':
      case 'flight-sales':
      case 'hotel-sales':
      case 'product-sales':
        return AuthorizationRules.SalesCore;
      default:
        return permission;
    }
  }, [permission, type]);

  const voidPermission =
    (can(permission) || can(purchaseCore) || can(salesCore)) && isActive;

  const onVoid = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? id;
      const message = {
        type,
        id: _id || 'undefined',
        message: 'Invalid Void Action (FE)',
      };

      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }

      dialog.showConfirmation({
        message: t('common:confirmation_void_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            onClose();

            const result = await voidMutate.mutateAsync({
              id: _id,
              type,
            });

            notification.success({ message: result?.message });
            onDismiss(_id);
            onRequestApproval(_id);
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [id, type, dialog, t, voidMutate, onDismiss, onRequestApproval],
  );

  const voidAction: PopoverMenuType = {
    label: t('common:void'),
    onClick: onVoid,
    isAllowed: voidPermission,
    isError: true,
    key: `void-${id}`,
  };

  const voidButton = (
    <DeleteButton
      onClick={onVoid}
      text={voidAction.label}
      isLoading={voidMutate.isLoading}
    />
  );

  return {
    voidAction,
    voidPermission,
    permission,
    voidButton,
    onVoid,
    voidMutate,
    isActive,
  };
}
